import { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import client from "../../../../api/client";
import { GlobalContext } from "../../../../context";
import Icon from "../Icon";
import { Avatar, Pill, ScopePicker, useScopes } from "./staffUi";

const errMsg = (e, fallback) => {
  const d = e?.response?.data;
  return (d?.errors && Object.values(d.errors)[0]?.[0]) || d?.message || fallback;
};

function Modal({ title, children, ok, busy, onCancel, onSubmit }) {
  return (
    <div className="sp-modal-wrap">
      <form className="sp-modal" role="dialog" aria-modal="true" aria-labelledby="cm-t" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        <h3 id="cm-t">{title}</h3>
        {children}
        <div className="sp-acts" style={{ justifyContent: "flex-end" }}>
          <button className="cbtn quiet" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="cbtn go" type="submit" disabled={busy}>{busy ? "Working…" : ok}</button>
        </div>
      </form>
    </div>
  );
}

function Brief({ data, scope, onSaved }) {
  const p = data.project;
  const [f, setF] = useState({ title: p?.title || "", description: p?.description || "", instructions: p?.instructions || "", due: p?.due || "", grace: p?.grace || "", showcase: p?.showcase || "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    if (!f.title.trim()) return toast.error("Give the capstone a title.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("cohort_id", scope.cohort_id); fd.append("course_id", scope.course_id);
      Object.entries(f).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (file) fd.append("file", file);
      await client.post("/staff/capstone/brief", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Brief saved");
      setFile(null);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "We couldn't save the brief."));
    }
    setBusy(false);
    return undefined;
  };
  const removeFile = async (id) => {
    try { await client.delete(`/staff/capstone/files/${id}`); toast.success("File removed"); onSaved(); } catch (e) { toast.error(errMsg(e, "We couldn't remove it.")); }
  };
  return (
    <section className="sp-box">
      <div className="sp-box-h"><h2>Brief</h2>{p && (p.released ? <Pill cls="ok">Released to students</Pill> : <Pill cls="gray">Not released yet</Pill>)}</div>
      <div className="sp-box-b sp-stack">
        <div className="sp-form two">
          <label className="sp-field" htmlFor="cb-title">Title<input id="cb-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Predict Lagos rental prices" /></label>
          <label className="sp-field" htmlFor="cb-sum">One-line summary<input id="cb-sum" maxLength={255} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="What teams build, in a sentence" /></label>
        </div>
        <label className="sp-field" htmlFor="cb-ins">What teams build and hand in<textarea id="cb-ins" rows={4} value={f.instructions} onChange={(e) => set("instructions", e.target.value)} /></label>
        <div className="sp-form three">
          <label className="sp-field" htmlFor="cb-due">Submission due (WAT)<input id="cb-due" type="datetime-local" value={f.due} onChange={(e) => set("due", e.target.value)} /></label>
          <label className="sp-field" htmlFor="cb-grace">Late submissions until (WAT)<input id="cb-grace" type="datetime-local" value={f.grace} onChange={(e) => set("grace", e.target.value)} /></label>
          <label className="sp-field" htmlFor="cb-show">Showcase (WAT)<input id="cb-show" type="datetime-local" value={f.showcase} onChange={(e) => set("showcase", e.target.value)} /></label>
        </div>
        <div><p className="sp-lbl">Brief files</p>
          {p?.files?.length ? <div className="sp-files">{p.files.map((m) => <div key={m.id} className="sp-file"><span className="sp-fico"><Icon name="files" size={16} /></span><a href={m.url} target="_blank" rel="noopener noreferrer">{m.name}</a><button type="button" className="link-btn danger" onClick={() => removeFile(m.id)}>Remove</button></div>)}</div> : <p className="course-tag" style={{ margin: 0 }}>No files yet.</p>}
          <label className="cbtn quiet" style={{ marginTop: 8, cursor: "pointer" }}>{file ? `Selected: ${file.name.slice(0, 30)}` : "+ Add a file (added when you save)"}<input type="file" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} /></label>
        </div>
        <div className="sp-acts" style={{ justifyContent: "flex-end" }}><button type="button" className="cbtn go" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save brief"}</button></div>
      </div>
    </section>
  );
}

/** One team's own project (teams often build different things). Shown to the team above the general brief. */
function TeamProject({ t, onChanged }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(t.title || "");
  const [brief, setBrief] = useState(t.brief || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTitle(t.title || ""); setBrief(t.brief || ""); }, [t]);
  const has = t.title || t.brief || t.files?.length;
  const save = async () => {
    setBusy(true);
    try { const r = await client.patch(`/staff/capstone/teams/${t.id}`, { title: title.trim() || null, brief: brief.trim() || null }); toast.success(r.data.message); onChanged(); setOpen(false); }
    catch (e) { toast.error(errMsg(e, "We couldn't save the project.")); }
    setBusy(false);
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { const r = await client.post(`/staff/capstone/teams/${t.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(r.data.message); onChanged(); }
    catch (e) { toast.error(errMsg(e, "We couldn't upload the file.")); }
  };
  const removeFile = async (id) => {
    try { await client.delete(`/staff/capstone/files/${id}`); toast.success("File removed"); onChanged(); } catch (e) { toast.error(errMsg(e, "We couldn't remove it.")); }
  };
  if (!open) {
    return (
      <div className="sp-tproj">
        <span className="sp-fico"><Icon name="task" size={14} /></span>
        <div>{has ? <><strong>{t.title || "Team project"}</strong><small>{t.brief ? `${t.brief.slice(0, 90)}${t.brief.length > 90 ? "…" : ""}` : ""}{t.files?.length ? ` · ${t.files.length} file${t.files.length === 1 ? "" : "s"}` : ""}</small></>
          : <small>Same project as everyone (the general brief)</small>}</div>
        <button type="button" className="link-btn" onClick={() => setOpen(true)}>{has ? "Edit" : "Give this team its own project"}</button>
      </div>
    );
  }
  return (
    <div className="sp-tproj open">
      <label className="sp-field" htmlFor={`tp-${t.id}`}>Project title<input id={`tp-${t.id}`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Customer churn prediction" maxLength={200} /></label>
      <label className="sp-field" htmlFor={`tb-${t.id}`}>What this team builds<textarea id={`tb-${t.id}`} rows={5} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="The problem, the dataset, what to deliver. The general brief still applies (deadline, showcase)." /></label>
      {t.files?.map((f) => <div key={f.id} className="sp-mem"><span className="sp-fico"><Icon name="files" size={14} /></span><span><a href={f.url} target="_blank" rel="noopener noreferrer">{f.name}</a></span><button type="button" className="link-btn" onClick={() => removeFile(f.id)}>Remove</button></div>)}
      <label className="cbtn quiet" style={{ justifySelf: "start", cursor: "pointer" }}>Add a file (dataset, brief…)<input type="file" hidden onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} /></label>
      <div className="sp-acts" style={{ justifyContent: "flex-end" }}>
        <button className="cbtn quiet" type="button" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
        <button className="cbtn go" type="button" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save team project"}</button>
      </div>
    </div>
  );
}

function Team({ t, teams, onChanged }) {
  const [name, setName] = useState(t.name);
  const [link, setLink] = useState(t.chat_link || "");
  useEffect(() => { setName(t.name); setLink(t.chat_link || ""); }, [t]);
  const patch = async (body, msg) => {
    try { await client.patch(`/staff/capstone/teams/${t.id}`, body); toast.success(msg); onChanged(); } catch (e) { toast.error(errMsg(e, "We couldn't save that.")); onChanged(); }
  };
  // After submission: correct the rep's "who took part" list.
  const setTook = async (m, participated) => {
    try {
      const r = await client.patch(`/staff/capstone/teams/${t.id}/participants`, { user_id: m.id, participated });
      toast.success(r.data.message); onChanged();
    } catch (e) { toast.error(errMsg(e, "We couldn't save that.")); }
  };
  const move = async (userId, toTeam) => {
    try { const r = await client.post(`/staff/capstone/teams/${toTeam}/members`, { user_id: userId }); toast.success(r.data.message); onChanged(); } catch (e) { toast.error(errMsg(e, "We couldn't move them.")); }
  };
  const remove = async () => {
    try { await client.delete(`/staff/capstone/teams/${t.id}`); toast.success("Team removed"); onChanged(); } catch (e) { toast.error(errMsg(e, "We couldn't remove it.")); }
  };
  return (
    <div className="sp-team">
      <div className="sp-team-h">
        <input aria-label="Team name" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== t.name && patch({ name: name.trim() }, "Team renamed")} disabled={t.submitted} />
        <span className="course-tag">{t.members.length} member{t.members.length === 1 ? "" : "s"}</span>
      </div>
      {t.submitted && <Pill cls={t.grade ? "ok" : "brand"}>{t.grade ? `Submitted · grade ${t.grade}` : "Submitted · being marked"}</Pill>}
      <div className="sp-tlink">
        <span className="sp-fico"><Icon name="chat" size={14} /></span>
        <input aria-label={`${t.name} group chat link`} placeholder="WhatsApp, Telegram or community link" value={link} onChange={(e) => setLink(e.target.value)} onBlur={() => link !== (t.chat_link || "") && patch({ chat_link: link.trim() || null }, "Group link saved")} />
      </div>
      <TeamProject t={t} onChanged={onChanged} />
      {t.members.map((m) => (
        <div key={m.id} className="sp-mem">
          <Avatar s={m} />
          <span><strong>{m.name}</strong>{t.rep_id === m.id ? <small className="sp-rep">Team rep · submits for the team</small>
            : t.submitted ? <small className={m.participated === false ? "sp-out" : "sp-in"}>{m.participated === false ? "Not included by the rep · doesn't share the grade" : "Took part · shares the grade"}</small>
            : <button type="button" className="link-btn" onClick={() => patch({ rep_user_id: m.id }, `${m.name} is now the team rep`)}>Make team rep</button>}</span>
          {t.submitted && t.rep_id !== m.id && (
            <button type="button" className="link-btn" onClick={() => setTook(m, m.participated === false)}>{m.participated === false ? "Include" : "Remove"}</button>
          )}
          {!t.submitted && (
            <select aria-label={`Move ${m.name}`} value="" onChange={(e) => e.target.value && move(m.id, e.target.value)}>
              <option value="">Move to…</option>
              {teams.filter((o) => o.id !== t.id && !o.submitted).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
        </div>
      ))}
      {!t.rep_id && t.members.length > 0 && <p className="sp-warnline">Pick a team rep. Only the rep can submit.</p>}
      {!t.members.length && <button type="button" className="link-btn danger" onClick={remove}>Remove empty team</button>}
    </div>
  );
}

/** Capstone & teams for a course: brief, teams with reps and chat links, release. */
/**
 * Which of the course's projects counts as the capstone. Beta cohorts had no capstone: their last
 * project counts automatically, and an admin can pick another. Its grade is used for certificates.
 */
function CapstoneChoice({ data, scope, isAdmin, onChanged }) {
  const choices = data.choices || [];
  if (choices.length < 2) return null;
  const current = data.project?.id;
  const pick = async (id) => {
    try { const r = await client.post("/staff/capstone/choose", { cohort_id: scope.cohort_id, course_id: scope.course_id, project_id: +id }); toast.success(r.data.message); onChanged(); }
    catch (e) { toast.error(errMsg(e, "We couldn't change the capstone.")); }
  };
  return (
    <section className="sp-box">
      <div className="sp-box-h"><h2>Which project counts as the capstone</h2><small>Its grade is a certificate requirement; the others count as activities</small></div>
      <div className="sp-rows">
        {choices.map((p) => (
          <div key={p.id}>
            <span className={`sp-fico ${p.id === current ? "ok" : ""}`}><Icon name={p.id === current ? "check" : "folder"} size={16} /></span>
            <div><strong>{p.title}</strong><small>{p.due ? `Due ${p.due.replace("T", " ")}` : "No due date"} · {p.submissions} submitted · {p.graded} graded{p.old ? " · set up the old way" : ""}</small></div>
            <span className="sp-acts">
              {p.id === current ? <Pill cls="ok">Capstone{p.chosen ? "" : " (automatic)"}</Pill>
                : isAdmin && <button type="button" className="link-btn" onClick={() => pick(p.id)}>Make this the capstone</button>}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function StaffCapstonePage() {
  const { authState: { data: auth } } = useContext(GlobalContext);
  const isAdmin = !!auth?.user?.is_admin;
  const { scopes, scope, setScope, failed } = useScopes();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!scope) return;
    client.get("/staff/capstone", { params: { cohort_id: scope.cohort_id, course_id: scope.course_id } })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e?.response?.data?.message || "We couldn't load the capstone."));
  }, [scope]);
  useEffect(() => { setData(null); load(); }, [load]);

  const post = async (url, body, after) => {
    setBusy(true);
    try { const r = await client.post(url, { cohort_id: scope.cohort_id, course_id: scope.course_id, ...body }); toast.success(r.data.message); setModal(null); load(); after?.(); }
    catch (e) { toast.error(errMsg(e, "That didn't work. Please try again.")); }
    setBusy(false);
  };
  const addTo = async (userId, teamId) => {
    try { const r = await client.post(`/staff/capstone/teams/${teamId}/members`, { user_id: userId }); toast.success(r.data.message); load(); } catch (e) { toast.error(errMsg(e, "We couldn't add them.")); }
  };

  const teams = data?.teams || [];
  const noRep = teams.filter((t) => !t.rep_id && t.members.length).length;

  return (
    <div className="lms"><div className="main single" style={{ maxWidth: 1240 }}><div className="view">
      <div className="sp-top">
        <div><span className="sp-kicker">{isAdmin ? "Learning" : "Teach"}</span><h1>Capstone &amp; teams</h1>
          <p>{data ? `${data.cohort.name} · ${data.course.title} · one team project, presented at the showcase` : "The capstone brief, teams and team reps for each course."}</p></div>
        <div className="sp-filters">
          <ScopePicker scopes={scopes} scope={scope} setScope={setScope} />
          {data?.project && (data.project.released ? <Pill cls="ok">Brief released to students</Pill>
            : <button type="button" className="cbtn go" onClick={() => setModal({ kind: "release" })}>Release brief to students</button>)}
        </div>
      </div>

      {failed ? <section className="complete-box"><div><strong>We couldn't load your courses</strong><small>Refresh the page to try again.</small></div></section>
        : error ? <section className="complete-box"><div><strong>{error}</strong><small>Check your connection and try again.</small></div><button className="cbtn quiet" type="button" onClick={load}>Try again</button></section>
        : !data ? <div className="skeleton" style={{ height: 360 }} />
        : !data.has_plan ? <section className="complete-box"><div><strong>This course doesn't have a plan yet</strong><small>The capstone lives in the plan's last weeks. Create the course plan first.</small></div></section>
        : (
          <>
            <CapstoneChoice data={data} scope={scope} isAdmin={isAdmin} onChanged={load} />
            <Brief key={`${scope.cohort_id}-${scope.course_id}-${data.project?.id || "new"}`} data={data} scope={scope} onSaved={load} />
            <section className="sp-box">
              <div className="sp-box-h"><h2>Teams</h2>
                {data.project && teams.length > 0 && <span className="sp-acts"><button type="button" className="cbtn quiet" onClick={() => setModal({ kind: "teams", size: 4, mode: "mix" })}>Recreate teams</button><button type="button" className="cbtn quiet" onClick={() => post("/staff/capstone/teams", {})}>+ Add team</button></span>}
              </div>
              {!data.project ? <div className="sp-box-b"><p className="course-tag" style={{ margin: 0 }}>Save the brief first, then create teams.</p></div>
                : !teams.length ? (
                  <div className="sp-box-b sp-empty-teams"><span className="sp-fico big"><Icon name="users" /></span>
                    <div><strong>No teams yet</strong><p className="course-tag" style={{ margin: "4px 0 12px" }}>{data.students} students in this course. Create teams automatically, then move people and change reps.</p>
                      <button type="button" className="cbtn go" onClick={() => setModal({ kind: "teams", size: 4, mode: "mix" })}>Create teams</button></div></div>
                ) : (
                  <>
                    <div className="sp-box-b"><div className="sp-teams">{teams.map((t) => <Team key={t.id} t={t} teams={teams} onChanged={load} />)}</div></div>
                    {data.unassigned.length > 0 && (
                      <div className="sp-box-b" style={{ borderTop: "1px solid var(--line)" }}>
                        <p className="sp-lbl">Not in a team · {data.unassigned.length}</p>
                        <div className="sp-unassigned">{data.unassigned.map((m) => (
                          <div key={m.id} className="sp-mem"><Avatar s={m} /><span><strong>{m.name}</strong></span>
                            <select aria-label={`Add ${m.name} to a team`} value="" onChange={(e) => e.target.value && addTo(m.id, e.target.value)}><option value="">Add to…</option>{teams.filter((t) => !t.submitted).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
                          </div>
                        ))}</div>
                      </div>
                    )}
                    <div className="sp-foot"><span>Members see their team, rep and group chat link on Projects once the brief is released. A team's rep and members are fixed once it submits.</span></div>
                  </>
                )}
            </section>
          </>
        )}

      {modal?.kind === "teams" && (
        <Modal title={teams.length ? "Recreate teams" : "Create teams"} ok={teams.length ? "Recreate teams" : "Create teams"} busy={busy} onCancel={() => setModal(null)}
          onSubmit={() => post("/staff/capstone/teams/generate", { size: +modal.size, mode: modal.mode })}>
          <div className="sp-form two">
            <label className="sp-field" htmlFor="tm-size">Students per team<select id="tm-size" value={modal.size} onChange={(e) => setModal({ ...modal, size: e.target.value })} autoFocus>{[2, 3, 4, 5, 6, 8].map((n) => <option key={n} value={n}>{n}</option>)}</select></label>
            <label className="sp-field" htmlFor="tm-mode">How to group<select id="tm-mode" value={modal.mode} onChange={(e) => setModal({ ...modal, mode: e.target.value })}><option value="mix">Mix by progress (each team gets stronger and newer students)</option><option value="random">Random</option></select></label>
          </div>
          <p className="course-tag" style={{ margin: 0 }}>{data.students} students → about {Math.ceil(data.students / modal.size)} teams. Each team's rep is suggested as the member with the best attendance and activities; you can change it.{teams.length ? " This replaces the current teams." : ""}</p>
        </Modal>
      )}
      {modal?.kind === "release" && (
        <Modal title="Release the brief?" ok="Release brief" busy={busy} onCancel={() => setModal(null)} onSubmit={() => post("/staff/capstone/release", {})}>
          <p>All {data.students} students see the brief{teams.length ? ", their team and their team rep" : ""} on the Projects page, and get a notification.</p>
          {noRep > 0 && <div className="sp-warn">{noRep} team{noRep > 1 ? "s have" : " has"} no rep yet. Pick reps first; only reps can submit.</div>}
          {!teams.length && <div className="sp-warn">There are no teams, so every student would submit on their own.</div>}
        </Modal>
      )}
    </div></div></div>
  );
}
